import type { TPersonAccessDto } from "@/types/backoffice/dto/person/person-access-dto.ts";
import type { TPersonDto } from "@/types/backoffice/dto/person/person-dto.ts";
import type { TRequiredAccess } from "@/types/common/access.ts";

import * as Sentry from "@sentry/vue";
import { defineStore } from "pinia";
import { ref } from "vue";

import { clearTokenDetailsFromLocalStorage } from "@/lib/helpers/authentication-helper.ts";

export const AUTHENTICATION_STORE_KEY = "AUTHENTICATION_STORE";

export function userHasAccess(accesses: {
  userAccesses: TPersonAccessDto[];
  requiredAccess: TRequiredAccess;
}): boolean {
  const { userAccesses, requiredAccess } = accesses;
  if (!userAccesses) {
    return false;
  }

  if (typeof requiredAccess === "string") {
    return userAccesses.some((_access) => _access.service === requiredAccess);
  }

  if (typeof requiredAccess === "number") {
    return userAccesses.some((_access) => _access.id === requiredAccess);
  }

  return userAccesses.some((_access) => {
    if (requiredAccess.content && _access.content !== requiredAccess.content) {
      return false;
    }

    if (requiredAccess.id && _access.id !== requiredAccess.id) {
      return false;
    }

    if (requiredAccess.service && _access.service !== requiredAccess.service) {
      return false;
    }

    if (requiredAccess.function && _access.function !== requiredAccess.function) {
      return false;
    }

    return true;
  });
}

export const useAuthenticationStore = defineStore(AUTHENTICATION_STORE_KEY, () => {
  const accesses = ref<Array<TPersonAccessDto> | undefined>(undefined);
  const me = ref<TPersonDto | undefined>(undefined);
  const hasOperatorExternalEndCallService = ref<boolean>(false);
  const loggedIn = ref(false);

  function login({
    me,
    accesses,
    hasOperatorExternalEndCallService,
  }: {
    me: TPersonDto;
    accesses: Array<TPersonAccessDto>;
    hasOperatorExternalEndCallService: boolean;
  }) {
    setAccesses(accesses);
    setMe(me);
    setHasOperatorExternalEndCallService(hasOperatorExternalEndCallService);
    Sentry.setUser({
      username: `${me.firstname}.${me.lastname}`,
      id: me.id,
    });
    loggedIn.value = true;
  }

  function setAccesses(next: Array<TPersonAccessDto>) {
    accesses.value = next;
  }

  function setMe(next: TPersonDto) {
    me.value = next;
  }

  function setHasOperatorExternalEndCallService(next: boolean) {
    hasOperatorExternalEndCallService.value = next;
  }

  function logout() {
    me.value = undefined;
    accesses.value = undefined;
    clearTokenDetailsFromLocalStorage();
    Sentry.setUser(null);
    loggedIn.value = false;
  }

  return {
    accesses,
    me,
    loggedIn,
    hasOperatorExternalEndCallService,
    login,
    logout,
    hasUserAccess: (requiredAccess: TRequiredAccess) =>
      userHasAccess({
        userAccesses: accesses.value ?? [],
        requiredAccess,
      }),
    setHasOperatorExternalEndCallService,
  };
});
